import React, { useState, useEffect } from 'react';
import { useWeb3ModalAccount } from '@web3modal/ethers/react';
import { useWeb3ModalProvider } from '@web3modal/ethers/react';
import { Contract, BrowserProvider } from 'ethers';
import Navbar from "../components/Navbar";
import emeABI from "../utils/emeABI.json";
import stakeABI from "../utils/StakeABI.json";
import vendorABI from "../utils/VendorABI.json";
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';
import AdminModal from '../components/AdminModal';

import { tokenAddress, vendorAddress, stakeAddress } from '../utils/contractAddress';

const About = () => {
  const navigate = useNavigate();
  const { walletProvider } = useWeb3ModalProvider();
  const { isConnected, address } = useWeb3ModalAccount();

  // Contract instances
  const [tokenContract, setTC] = useState();
  const [vendorContract, setVC] = useState();
  const [stakeContract, setSC] = useState();

  // ========= Token Admin Panel States =========
  const [maxWalletAmount, setMaxWalletAmount] = useState('');
  const [swapAmount, setSwapAmount] = useState('');
  const [excludeAddress, setExcludeAddress] = useState('');
  const [ammPairAddress, setAmmPairAddress] = useState('');
  const [ammPairValue, setAmmPairValue] = useState('true');
  const [buyFee, setBuyFee] = useState('');
  const [sellFee, setSellFee] = useState('');
  const [excludeFeeAddress, setExcludeFeeAddress] = useState('');
  const [operationsAddress, setOperationsAddress] = useState('');
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  
  // ========= Vendor Admin Panel States =========
  const [vsStage0Cap, setVsStage0Cap] = useState('');
  const [vsStage1Cap, setVsStage1Cap] = useState('');
  const [vsStage2Cap, setVsStage2Cap] = useState('');
  const [vsStage0Price, setVsStage0Price] = useState('');
  const [vsStage1Price, setVsStage1Price] = useState('');
  const [vsStage2Price, setVsStage2Price] = useState('');

  // ========= Staking Admin Panel States =========
  const [stakeAPY, setStakeAPY] = useState('');

  // ========= Global Modal Transaction States =========
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalTxHash, setModalTxHash] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // Check if connected wallet is the owner (using tokenContract.owner())

  // Build contract instances when walletProvider is available
  useEffect(() => {
    if (walletProvider && isConnected) {
      buildContracts();
    }
  }, [walletProvider, isConnected]);

  const buildContracts = async () => {
    try {
      const ethersProvider = new BrowserProvider(walletProvider);
      const signer = await ethersProvider.getSigner();
      const tokenC = new Contract(tokenAddress, emeABI, signer);
      const vendorC = new Contract(vendorAddress, vendorABI, signer);
      const stakeC = new Contract(stakeAddress, stakeABI, signer);
      // const vestingC = new Contract(vestingAddress, vestingABI, signer);

      setTC(tokenC);
      setVC(vendorC);
      setSC(stakeC);
      // setVSC(vestingC);
      if(tokenC && address) {
        try {
          const ownerAddress = await tokenC.owner();
          if (ownerAddress.toLowerCase() !== address.toLowerCase()) {
            navigate('/');
          }
        } catch (error) {
          console.error("Error checking contract owner:", error);
        }
      } else{navigate('/');}
    } catch (error) {
      console.log(error);
    }
  };

  // Helper to execute a contract function and update modal state.
  const executeTx = async (fn, successMsg) => {
    try {
      setModalOpen(true);
      setModalLoading(true);
      setModalTitle("Processing Transaction");
      setModalMessage("Please wait...");
      setModalTxHash('');
      setModalError('');
      const tx = await fn();
      const receipt = await tx.wait();
      setModalTitle("Transaction Successful");
      setModalMessage(successMsg);
      setModalTxHash(receipt.transactionHash);
    } catch (error) {
      setModalTitle("Transaction Failed");
      setModalMessage("There was an error processing your transaction.");
      setModalError(error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ========== Token Admin Functions ==========
  const handleEnableTrading = async () => {
    await executeTx(() => tokenContract.enableTrading(), 'Trading enabled!');
  };

  const handleRemoveLimits = async () => {
    await executeTx(() => tokenContract.removeLimits(), 'Limits removed!');
  };

  const handleUpdateMaxWalletAmount = async () => {
    try {
      const newAmount = ethers.parseUnits(maxWalletAmount, 18);
      await executeTx(() => tokenContract.updateMaxWalletAmount(newAmount), 'Max wallet amount updated!');
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateSwapTokensAtAmount = async () => {
    try {
      const newAmount = ethers.parseUnits(swapAmount, 18);
      await executeTx(() => tokenContract.updateSwapTokensAtAmount(newAmount), 'Swap tokens amount updated!');
    } catch (error) {
      console.error(error);
    }
  };

  const handleExcludeFromMaxTransaction = async () => {
    await executeTx(
      () => tokenContract.excludeFromMaxTransaction(excludeAddress, true),
      'Address excluded from max transactions!'
    );
  };

  const handleSetAutomatedMarketMakerPair = async () => {
    const boolVal = ammPairValue === 'true';
    await executeTx(
      () => tokenContract.setAutomatedMarketMakerPair(ammPairAddress, boolVal),
      'Automated market maker pair set!'
    );
  };

  const handleUpdateBuyFees = async () => {
    await executeTx(() => tokenContract.updateBuyFees(buyFee), 'Buy fee updated!');
  };

  const handleUpdateSellFees = async () => {
    await executeTx(() => tokenContract.updateSellFees(sellFee), 'Sell fee updated!');
  };

  const handleExcludeFromFees = async () => {
    await executeTx(
      () => tokenContract.excludeFromFees(excludeFeeAddress, true),
      'Address excluded from fees!'
    );
  };

  const handleForceSwapBackTokens = async () => {
    await executeTx(() => tokenContract.forceSwapBackTokens(), 'Force swap back executed!');
  };

  const handleSetOperationsAddress = async () => {
    await executeTx(() => tokenContract.setOperationsAddress(operationsAddress), 'Operations address updated!');
  };

  const handleTransferOwnership = async () => {
    await executeTx(() => tokenContract.transferOwnership(newOwnerAddress), 'Ownership transferred!');
  };

  // ========== Vendor Admin Functions ==========
  const handleStartPresaleVendor = async () => {
    try {
      const s0Cap = ethers.parseUnits(vsStage0Cap, 18);
      const s1Cap = ethers.parseUnits(vsStage1Cap, 18);
      const s2Cap = ethers.parseUnits(vsStage2Cap, 18);
      const s0Price = ethers.parseUnits(vsStage0Price, 18);
      const s1Price = ethers.parseUnits(vsStage1Price, 18);
      const s2Price = ethers.parseUnits(vsStage2Price, 18);
      await executeTx(() => vendorContract.startPresale(s0Cap, s1Cap, s2Cap, s0Price, s1Price, s2Price), 'Presale started on vendor contract!');
    } catch (error) {
      console.error(error);
    }
  };

  const handleActivateVestingVendor = async () => {
    await executeTx(() => vendorContract.activateVesting(), 'Vendor vesting activated!');
  };

  const handleEndPresaleVendor = async () => {
    await executeTx(() => vendorContract.endPresale(), 'Vendor presale ended!');
  };

  const handleWithdrawFundsVendor = async () => {
    await executeTx(() => vendorContract.withdrawFunds(), 'Vendor funds withdrawn!');
  };

  const handleWithdrawRemainingTokensVendor = async () => {
    await executeTx(() => vendorContract.withdrawRemainingTokens(), 'Vendor remaining tokens withdrawn!');
  };

  // ========== Staking Admin Functions ==========

  const handleEnableStaking = async () => {
    await executeTx(() => stakeContract.enableStaking(), 'Staking enabled!');
  };

  const handleSetAPY = async () => {
    try {
      await executeTx(() => stakeContract.setAPY(stakeAPY), 'APY updated!');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <Navbar vContract={vendorContract}/>
      <h2>About</h2>
      <p>This page provides more information about the Secure React App.</p>

      {/* Token Admin Panel */}
      <div className="owner-panel">
        <h2>Token Admin Panel</h2>
        
        <div className="admin-section">
          <button onClick={handleEnableTrading} className="admin-button">Abilita Trading</button>
          <button onClick={handleRemoveLimits} className="admin-button">Rimuovi Limiti</button>
        </div>

        <div className="admin-section">
          <label>Imposta limite token per wallet:</label>
          <input
            type="number"
            className="admin-input"
            value={maxWalletAmount}
            onChange={(e) => setMaxWalletAmount(e.target.value)}
            placeholder="e.g., 1000"
          />
          <button onClick={handleUpdateMaxWalletAmount} className="admin-button">Update</button>
        </div>

        <div className="admin-section">
          <label>Imposta quantitativo per scambio tasse automatico:</label>
          <input
            type="number"
            className="admin-input"
            value={swapAmount}
            onChange={(e) => setSwapAmount(e.target.value)}
            placeholder="e.g., 50"
          />
          <button onClick={handleUpdateSwapTokensAtAmount} className="admin-button">Update</button>
        </div>

        <div className="admin-section">
          <label>Escludi da controllo totale token posseduti (Address):</label>
          <input
            type="text"
            className="admin-input"
            value={excludeAddress}
            onChange={(e) => setExcludeAddress(e.target.value)}
            placeholder="0x..."
          />
          <button onClick={handleExcludeFromMaxTransaction} className="admin-button">Exclude</button>
        </div>

        <div className="admin-section">
          <label>Imposta AMM (Liquidity pool address):</label>
          <input
            type="text"
            className="admin-input"
            value={ammPairAddress}
            onChange={(e) => setAmmPairAddress(e.target.value)}
            placeholder="Pair Address"
          />
          <select
            value={ammPairValue}
            onChange={(e) => setAmmPairValue(e.target.value)}
            className="admin-input"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
          <button onClick={handleSetAutomatedMarketMakerPair} className="admin-button">Set</button>
        </div>

        <div className="admin-section">
          <label>Imposta tasse sull'acquisto (%):</label>
          <input
            type="number"
            className="admin-input"
            value={buyFee}
            onChange={(e) => setBuyFee(e.target.value)}
            placeholder="e.g., 1"
          />
          <button onClick={handleUpdateBuyFees} className="admin-button">Update</button>
        </div>

        <div className="admin-section">
          <label>Imposta tasse sulla vendita (%):</label>
          <input
            type="number"
            className="admin-input"
            value={sellFee}
            onChange={(e) => setSellFee(e.target.value)}
            placeholder="e.g., 3"
          />
          <button onClick={handleUpdateSellFees} className="admin-button">Update</button>
        </div>

        <div className="admin-section">
          <label>Escludi da Tasse (Address):</label>
          <input
            type="text"
            className="admin-input"
            value={excludeFeeAddress}
            onChange={(e) => setExcludeFeeAddress(e.target.value)}
            placeholder="0x..."
          />
          <button onClick={handleExcludeFromFees} className="admin-button">Exclude</button>
        </div>

        <div className="admin-section">
          <button onClick={handleForceSwapBackTokens} className="admin-button">
            Forza prelievo tasse
          </button>
        </div>

        <div className="admin-section">
          <label>Imposta wallet per ricezione tasse:</label>
          <input
            type="text"
            className="admin-input"
            value={operationsAddress}
            onChange={(e) => setOperationsAddress(e.target.value)}
            placeholder="0x..."
          />
          <button onClick={handleSetOperationsAddress} className="admin-button">Update</button>
        </div>

        <div className="admin-section">
          <label>Cambi Ownership (New Owner Address):</label>
          <input
            type="text"
            className="admin-input"
            value={newOwnerAddress}
            onChange={(e) => setNewOwnerAddress(e.target.value)}
            placeholder="0x..."
          />
          <button onClick={handleTransferOwnership} className="admin-button">Transfer</button>
        </div>
      </div>

      {/* Vendor Admin Panel */}
      <div className="owner-panel">
        <h2>Presale Admin Panel</h2>
        
        <div className="admin-section">
          <label>Stage 0 Tokens:</label>
          <input
            type="number"
            className="admin-input"
            value={vsStage0Cap}
            onChange={(e) => setVsStage0Cap(e.target.value)}
            placeholder="e.g., 1000"
          />
          <label>Stage 0 Prezzo (USD per token):</label>
          <input
            type="number"
            className="admin-input"
            value={vsStage0Price}
            onChange={(e) => setVsStage0Price(e.target.value)}
            placeholder="e.g., 0.03"
          />
        </div>
        <div className="admin-section">
          <label>Stage 1 Tokens:</label>
          <input
            type="number"
            className="admin-input"
            value={vsStage1Cap}
            onChange={(e) => setVsStage1Cap(e.target.value)}
            placeholder="e.g., 2000"
          />
          <label>Stage 1 Prezzo (USD per token):</label>
          <input
            type="number"
            className="admin-input"
            value={vsStage1Price}
            onChange={(e) => setVsStage1Price(e.target.value)}
            placeholder="e.g., 0.04"
          />
        </div>
        <div className="admin-section">
          <label>Stage 2 Tokens:</label>
          <input
            type="number"
            className="admin-input"
            value={vsStage2Cap}
            onChange={(e) => setVsStage2Cap(e.target.value)}
            placeholder="e.g., 3000"
          />
          <label>Stage 2 Prezzo (USD per token):</label>
          <input
            type="number"
            className="admin-input"
            value={vsStage2Price}
            onChange={(e) => setVsStage2Price(e.target.value)}
            placeholder="e.g., 0.05"
          />
        </div>
        <div className="admin-section">
          <button onClick={handleStartPresaleVendor} className="admin-button">
            Abilita Presale
          </button>
        </div>

        <div className="admin-section">
          <button onClick={handleActivateVestingVendor} className="admin-button">
            Abilita Vesting
          </button>
        </div>

        <div className="admin-section">
          <button onClick={handleEndPresaleVendor} className="admin-button">
            Termina Presale
          </button>
        </div>

        <div className="admin-section">
          <button onClick={handleWithdrawFundsVendor} className="admin-button">
            Preleva USDT/BNB
          </button>
        </div>

        <div className="admin-section">
          <button onClick={handleWithdrawRemainingTokensVendor} className="admin-button">
            Preleva Token Rimanenti in Presale
          </button>
        </div>
      </div>

      {/* Staking Admin Panel */}
      <div className="owner-panel">
        <h2>Staking Admin Panel</h2>
        <div className="admin-section">
          <button onClick={handleEnableStaking} className="admin-button">
            Abilita Staking
          </button>
        </div>
        <div className="admin-section">
          <label>Imposta Interesse Annuo (e.g., 1000 = 10%):</label>
          <input
            type="number"
            className="admin-input"
            value={stakeAPY}
            onChange={(e) => setStakeAPY(e.target.value)}
            placeholder="e.g., 1000"
          />
          <button onClick={handleSetAPY} className="admin-button">
            Aggiorna APY
          </button>
        </div>
      </div>

      {/* Admin Modal for Transaction Status */}
      <AdminModal
        isOpen={modalOpen}
        title={modalTitle}
        message={modalMessage}
        txHash={modalTxHash}
        error={modalError}
        isLoading={modalLoading}
        onClose={() => {
          setModalOpen(false);
          setModalTitle('');
          setModalMessage('');
          setModalTxHash('');
          setModalError('');
          setModalLoading(false);
        }}
      />
    </div>
  );
};

export default About;
